import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import { Hooks } from '@grandvisionhq/carts-v2';
import { toast } from '@grandvisionhq/common';
import { useMutation } from '@grandvisionhq/graphql';
import { useDispatch } from '@grandvisionhq/state';
import { errorsMapping } from '../../mappers/errors-mapping.js';
import { selectShippingMethod } from '../../StepShipping/actions.js';
import { useMutationConfig } from './use-mutation-config.js';
import { useUpdateCartCache } from './use-update-cart-cache.js';
import { getFlattenedCartFailureErrorCodes } from '../../utils/get-flattened-cart-error-codes.js';
import { SET_SHIPPING_METHOD } from '../../operations/mutations/set-shipping-method.js';

const useSetShippingMethod = ({ onError , onCompleted  } = {})=>{
    const [{ cartId , cartHash  }] = Hooks.useCartStorage();
    const { updateCacheWithCart  } = useUpdateCartCache();
    const [selectedShippingMethod, setSelectedShippingMethod] = React__default.useState(undefined);
    const dispatch = useDispatch();
    /**
   * Handle success and error response from mutation operation
   * @param responseData
   */ const handleOnCompleted = React__default.useCallback((responseData)=>{
        let result;
        if ('setShippingMethod' in responseData) {
            var ref;
            result = responseData.setShippingMethod;
            if ((ref = result.failures) === null || ref === void 0 ? void 0 : ref.length) {
                const errorCodes = getFlattenedCartFailureErrorCodes(result.failures);
                onError === null || onError === void 0 ? void 0 : onError({
                    reason: 'MUTATION'
                });
                /**
           *  Notify user about all failures
           *  To handle granular errors return from mutation operation
           */ errorCodes.forEach((error)=>toast.failed(error)
                );
            }
            if (result.success) {
                dispatch(selectShippingMethod({
                    shippingMethod: {
                        method: selectedShippingMethod,
                        isValid: true,
                        isTouched: true
                    }
                }));
                onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted('MUTATION');
            }
        }
    }, [
        dispatch,
        onCompleted,
        onError,
        selectedShippingMethod
    ]);
    const handleOnError = React__default.useCallback(()=>{
        onError === null || onError === void 0 ? void 0 : onError({
            reason: 'MUTATION'
        });
        // General error for apollo error response
        toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
    }, [
        onError
    ]);
    const updateCache = React__default.useCallback((cache, { data  })=>{
        if (data && data.setShippingMethod.success) {
            const updatedCart = data.setShippingMethod.cart;
            updatedCart && updateCacheWithCart({
                cart: updatedCart,
                cache
            });
        }
    }, [
        updateCacheWithCart
    ]);
    const { mutationOptions  } = useMutationConfig({
        onError: handleOnError,
        onCompleted: handleOnCompleted
    });
    const [mutationCallback, mutation] = useMutation(SET_SHIPPING_METHOD, swcHelpers.objectSpread({}, mutationOptions, {
        update: updateCache,
        context: {
            forwardHeaders: {
                'cart-hash': cartHash
            }
        }
    }));
    /**
   *  Shipping method data update operation
   *  @param: shippingMethodId(UUID)
   */ const setShippingMethod = React__default.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(shippingMethodId) {
            yield mutationCallback({
                variables: {
                    shippingMethodInput: {
                        shippingMethodId,
                        checkoutId: cartId
                    }
                }
            });
        });
        return function(shippingMethodId) {
            return _ref.apply(this, arguments);
        };
    }(), [
        mutationCallback,
        cartId
    ]);
    return {
        setShippingMethod,
        isUpdating: mutation.loading,
        updateError: mutation.error,
        setSelectedShippingMethod
    };
};

export { useSetShippingMethod };
//# sourceMappingURL=use-set-shipping-method.js.map
